/**
 * Admin → Groups (container): the list (live) and "New group" — a name, then
 * the group's page for its permissions, scope and members.
 */
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useAdminGroups, useAdminSites, useAdminUsers, useCreateGroup, useStudios } from '../../app/hooks.js';
import { groupHref, GroupsTable } from '../../components/admin/GroupsTable.js';
import { NameDialog } from '../../components/admin/NameDialog.js';
import { errorText } from './common.js';

export function GroupsAdminView() {
  const groups = useAdminGroups();
  const users = useAdminUsers();
  const sites = useAdminSites();
  const studios = useStudios();
  const create = useCreateGroup();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);

  const memberCounts = new Map<string, number>();
  for (const user of users.data ?? []) for (const groupId of user.groupIds) memberCounts.set(groupId, (memberCounts.get(groupId) ?? 0) + 1);
  const names = { sites: new Map((sites.data ?? []).map((s) => [s.siteId, s.name])), studios: new Map((studios.data ?? []).map((s) => [s.studioId, s.name])) };

  return (
    <>
      <GroupsTable
        groups={groups.data ?? []}
        memberCounts={memberCounts}
        names={names}
        loading={groups.isFetching}
        error={errorText(groups.error)}
        onCreate={() => {
          create.reset();
          setCreating(true);
        }}
        onRetry={() => {
          void groups.refetch();
        }}
        onOpen={(groupId) => {
          void navigate({ to: groupHref(groupId) });
        }}
      />
      <NameDialog
        open={creating}
        title="New group"
        description="It starts with no permissions, tenant-wide; set its permissions, scope and members on its page."
        label="Name"
        initial=""
        submitLabel="Create group"
        pending={create.isPending}
        error={errorText(create.error)}
        onSubmit={(name) =>
          create.mutate(
            { name },
            {
              onSuccess: (group) => {
                setCreating(false);
                void navigate({ to: groupHref(group.groupId) });
              },
            },
          )
        }
        onOpenChange={setCreating}
      />
    </>
  );
}
